// src/encounter/lib/to-questionnaire-response-resource.ts
// Map the shared encounter QuestionnaireResponse (DS flat linkId→value answers) → the FHIR-native
// QuestionBank `QuestionnaireResponseResource` for `POST/PUT /api/questionnaire-responses`
// (PROD-1159 create · PROD-1354c upsert + mapper fidelity).
// One QuestionnaireResponse spans ALL sections (HPI/ROS/PE/Risk) — the whole shared response is
// persisted, so editing any section round-trips. Tenant is server-stamped; we never send it.
//
// Mapper fidelity (PROD-1354c, FE-only — the BE DTO already carries these):
//  • valueCoding — a choice answer whose key matches an answerOption coding persists as the FULL
//    coding (system/code/display), not a bare valueString. Reload restores the key via
//    `code ?? display` (from-questionnaire-response-resource.ts), so it round-trips.
//  • decimal-vs-integer — a `decimal`-typed item answered with a whole number (e.g. 5) persists
//    as valueDecimal (the item type wins over the numeric heuristic).
// Both need the Questionnaire definition → pass the optional `itemMeta` (buildQrItemMeta).
// Without meta the v1 heuristics apply unchanged (valueString / Number.isInteger).
//
// PROD-2129 (D-S10b): `itemMeta` also carries `readOnly`, and a read-only item is DROPPED — a
// readout is presented, never answered, so it must not appear in `item[]`. Without meta nothing
// can be identified as read-only and nothing is dropped.
import type { QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import { noteDeltaExtension, noteDeltaFits } from './note-delta-extension';
import type {
  QuestionnaireResponseResource,
  QRItemWire,
  QRAnswerWire,
  QRExtensionWire,
} from '@/types/questionnaire-response-save-api';
import { codingKey, type QrItemMeta, type QrItemMetaMap } from './qr-item-meta';

export interface QuestionnaireResourceContext {
  /** The Questionnaire definition id (the resolved encounter template). */
  questionnaireId: string;
  /** Patient id → `subject: { reference: "Patient/{id}" }`. */
  patientId: string;
  /** Encounter id → `encounter: { reference: "Encounter/{id}" }`. */
  encounterId: string;
  /**
   * The stored QR document id (from the reload GET or a prior save's response). Present → the
   * caller PUTs `/{id}` (QB tenant-scoped upsert, PROD-1354c) and the same row is overwritten;
   * absent → POST creates.
   */
  id?: string;
  /** FHIR status; defaults to 'in-progress' (the encounter is still open). */
  status?: 'in-progress' | 'completed' | 'amended';
  /** ISO authored timestamp (optional; the server stamps lastUpdated regardless). */
  authored?: string;
  /**
   * PROD-2961 (frame v2): each note line's Delta with pieces, by linkId (note-line-delta.ts). Sent as the
   * item's `cps-note-delta` extension ONLY while it still derives the answer's text; a stale one is
   * dropped and the plain text wins. Absent or empty → the wire is exactly as before.
   */
  noteDeltas?: Readonly<Record<string, string>>;
  /**
   * PROD-2608. Stored items the current form has no place for (see `orphaned-answers.ts`), written
   * back EXACTLY as they were saved — never re-derived from the form — so an answer whose question
   * was edited out of the template survives every autosave and amendment with its wire shape intact.
   * An item whose linkId the form itself answers is skipped: the form's answer is the newer one.
   */
  preserved?: readonly QRItemWire[];
}

/** A choice key → the full answerOption coding when it matches, else a bare valueString. */
function codedOrString(v: string, meta?: QrItemMeta): QRAnswerWire {
  const coding = meta?.options.find((c) => codingKey(c) === v);
  if (coding) {
    return {
      valueCoding: {
        ...(coding.system != null ? { system: coding.system } : {}),
        ...(coding.code != null ? { code: coding.code } : {}),
        ...(coding.display != null ? { display: coding.display } : {}),
      },
    };
  }
  return { valueString: v };
}

/** A single DS answer value → one-or-more FHIR answer entries (value* polymorphism). */
function toAnswers(value: unknown, meta?: QrItemMeta): QRAnswerWire[] {
  if (value === undefined || value === null || value === '') return [];
  if (Array.isArray(value)) {
    // Multi-select (choice/open-choice) → one answer per selected value; each selection gets the
    // same coding lookup as a single choice (PROD-1354c valueCoding fidelity).
    return value
      .filter((v) => v !== undefined && v !== null && v !== '')
      .map((v) => codedOrString(String(v), meta));
  }
  if (typeof value === 'boolean') return [{ valueBoolean: value }];
  if (typeof value === 'number') {
    // The item TYPE wins (PROD-1354c decimal-vs-integer): a decimal question answered with a
    // whole number is still a decimal. An integer item only claims genuinely whole values —
    // a fractional number on it degrades to valueDecimal rather than an INVALID
    // valueInteger: 1.5 (Codex MINOR). Fall back to the numeric heuristic when meta is absent.
    if (meta?.type === 'decimal') return [{ valueDecimal: value }];
    if (meta?.type === 'integer' && Number.isInteger(value)) return [{ valueInteger: value }];
    if (meta?.type === 'integer') return [{ valueDecimal: value }];
    return [Number.isInteger(value) ? { valueInteger: value } : { valueDecimal: value }];
  }
  if (value instanceof Date) return [{ valueDateTime: value.toISOString() }];
  // Strings: free-text AND choice keys (the DS renderer stores the option's `code ?? display`).
  // With meta, a key that matches an answerOption graduates to the full valueCoding.
  return [codedOrString(String(value), meta)];
}

/**
 * The AI-provenance extension, on the ANSWER (PROD-2537).
 *
 * Persisted because it was not, and that was a clinical-attribution defect: after autosave and
 * reopening the encounter, `fromQuestionnaireResponseResource` rebuilt answers with no
 * `aiAuthored`, so the DS-021 cue vanished and model-drafted prose presented as the clinician's
 * own. Found by cross-model review.
 *
 * THE URL IS QUESTIONBANK'S, NOT OURS. QuestionBank #155 made `item.extension` survive the store
 * and published the canonical url for exactly this fact (`AmbientExtensions.AnswerProvenanceUrl`,
 * `Scrips.QuestionBank/Application/Abstractions/Dtos/AmbientExtensions.cs`): a COMPLEX extension
 * whose `machine-suggested` sub-extension says the answer was suggested rather than typed. This
 * file used to write its own flat `…/ai-authored` boolean — a second spelling for one fact, which
 * is how a writer and a reader drift apart with every test still green. The writer now emits only
 * the canonical form; the reader accepts both, so a row saved under the old url still rehydrates.
 *
 * Only `machine-suggested` is sent. `source-turn` (the transcript turn id) is defined too, but the
 * answer model carries no turn id today, and inventing one would be worse than omitting it.
 */
export const ANSWER_PROVENANCE_URL = 'https://scrips.com/fhir/StructureDefinition/ambient-answer-provenance';
/** Sub-extension of {@link ANSWER_PROVENANCE_URL}: `valueBoolean`, true = machine-suggested. */
export const MACHINE_SUGGESTED_SUB_URL = 'machine-suggested';
/**
 * The LEGACY flat url this client wrote before PROD-2537. Read, never written: rows saved with it
 * (QuestionBank has persisted item extensions since #155) must keep their DS-021 cue on reload.
 *
 * TODO(PROD-2618): remove this and its branch in `isMachineSuggested` once stored rows carrying it
 * are rewritten or the window is declared closed.
 */
export const LEGACY_AI_AUTHORED_URL = 'https://fhir.scrips.com/StructureDefinition/ai-authored';

/** The canonical provenance extension for one machine-suggested answer. */
export function machineSuggestedExtension(): QRExtensionWire {
  return {
    url: ANSWER_PROVENANCE_URL,
    // `source-turn` is INTENTIONALLY OMITTED: the answer model carries no transcript turn id. Add
    // it only from a real turn id on the answer, never from text or an index guessed here.
    extension: [{ url: MACHINE_SUGGESTED_SUB_URL, valueBoolean: true }],
  };
}

/** Does this item's extension list say the answer was machine-suggested? (Either spelling.) */
export function isMachineSuggested(extension: readonly QRExtensionWire[] | null | undefined): boolean {
  return (extension ?? []).some(
    (e) =>
      (e.url === LEGACY_AI_AUTHORED_URL && e.valueBoolean === true) ||
      (e.url === ANSWER_PROVENANCE_URL &&
        (e.extension ?? []).some((sub) => sub.url === MACHINE_SUGGESTED_SUB_URL && sub.valueBoolean === true)),
  );
}

/** The item's extensions: AI provenance when true, the line's Delta when it fits; no key when neither. */
function itemExtensions(
  aiAuthored: boolean | undefined,
  delta: string | undefined,
  value: unknown,
  encounterId: string | undefined,
): { extension?: QRExtensionWire[] } {
  const extension = [...(aiAuthored ? [machineSuggestedExtension()] : []), ...(noteDeltaFits(delta, value, encounterId) ? [noteDeltaExtension(delta)] : [])];
  return extension.length ? { extension } : {};
}

/** Build the FHIR QuestionnaireResponse resource for the QB create/upsert endpoint. */

export function toQuestionnaireResponseResource(
  response: QuestionnaireResponse,
  ctx: QuestionnaireResourceContext,
  itemMeta?: QrItemMetaMap,
): QuestionnaireResponseResource {
  const item: QRItemWire[] = response.answers
    /* READ-ONLY ITEMS NEVER LEAVE (PROD-2129, D-S10 condition b).
     *
     * A `readOnly` item is a READOUT — the Studio authors it as a quantity carrying a LOINC code
     * and a unit, whose value is computed or sourced elsewhere and only PRESENTED to the
     * clinician. The DS renderer nevertheless holds a value against its linkId (a
     * calculatedExpression writes one), and this mapper used to persist every linkId in
     * `response.answers` with no idea which were questions. The consequence: the QR asserted a
     * clinician-entered answer for a value nobody entered — a provenance defect on a resource
     * whose whole purpose is to record what the clinician answered.
     *
     * NOT claimed: that a stored readout was then presented as current. For an item carrying a
     * `calculatedExpression` the DS renderer computes the value itself (`qrComputeCalculated`) and
     * presents the computed one, so a stale stored number could not surface that way. IF a readout
     * were ever sourced without a calc expression the render question would reopen — unmeasured,
     * and not the reason for this filter.
     *
     * THE PREDICATE IS `readOnly`, BY CONTRACT (D-S10 b) — not "is it computed". An item with a
     * `calculatedExpression` but no `readOnly: true` still persists its computed value as an
     * answer, deliberately: a scored total the author left writable is an answer the clinician may
     * override, and only the authored flag says which it is.
     *
     * THE READ SIDE IS UNFILTERED (`from-questionnaire-response-resource.ts`). A legacy row
     * written before this filter still hydrates its readout linkId once on reload; the next
     * autosave drops it, because this filter runs over the WHOLE response and not a delta. No
     * migration is needed, and none is implied.
     *
     * The filter runs off `itemMeta`, the same Questionnaire-derived lookup the valueCoding and
     * decimal-vs-integer fidelity already use, so no caller signature changes and no second
     * source of truth appears. DEGRADATION, stated plainly: with no `itemMeta` the mapper cannot
     * know which items are read-only and filters nothing — identical to the v1 heuristics posture
     * for `type` and `options`. Every production call site passes meta (the encounter shell builds
     * it from the resolved template); the meta-less path is tests and the ambient orphan sink. */
    .filter((a) => itemMeta?.get(a.linkId)?.readOnly !== true)
    .map((a) => ({
      linkId: a.linkId,
      // PROD-2608: the question as it read when answered, so the answer stays readable if the
      // template later drops the question. Only when the template authored text.
      ...(itemMeta?.get(a.linkId)?.text ? { text: itemMeta.get(a.linkId)!.text } : {}),
      answer: toAnswers(a.value, itemMeta?.get(a.linkId)),
      // Only when true — an extension asserting `false` on every clinician-typed answer would
      // triple the payload to say nothing. PROD-2961: a line's Delta with pieces rides beside its text.
      ...itemExtensions(a.aiAuthored, ctx.noteDeltas?.[a.linkId], a.value, ctx.encounterId),
    }))
    .filter((it) => it.answer.length > 0); // drop unanswered linkIds

  if (ctx.preserved?.length) {
    const answered = new Set(item.map((it) => it.linkId));
    for (const kept of ctx.preserved) {
      if (!answered.has(kept.linkId)) item.push(kept);
    }
  }

  return {
    resourceType: 'QuestionnaireResponse',
    ...(ctx.id ? { id: ctx.id } : {}),
    questionnaire: ctx.questionnaireId,
    status: ctx.status ?? 'in-progress',
    subject: { reference: `Patient/${ctx.patientId}` },
    encounter: { reference: `Encounter/${ctx.encounterId}` },
    ...(ctx.authored ? { authored: ctx.authored } : {}),
    item,
  };
}
